// Create export.service.ts
import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ColDef } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from '@services/alert.service';
import { AddCommentsComponent } from '@app/pages/asset-management/cwr-offboardingCard/Offboarding-DetailsScreen/add-comments/add-comments.component';

const CENTER_HEADERS = new Set([
  'Total Working Hrs',
  'Billable Hrs',
  'Leaves Availed',
  'Paid Leaves',
  'Unpaid Leaves',
]);

const CENTER_COL_WIDTH = 15;
function padCenter(value: any): string {
  const str = value !== null && value !== undefined ? String(value) : '';
  const pad = Math.max(0, Math.floor((CENTER_COL_WIDTH - str.length) / 1));
  return ' '.repeat(pad) + str;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  constructor(
    private dialog: MatDialog,
    private alertService: AlertService
  ) {}

  exportToExcel(data: any[], columns: ColDef[], fileName: string, sheetName: string = 'Data'): void {
    const exportData = this.prepareDataForExport(data, columns);
    const workbook = this.createWorkbook(exportData, sheetName);
    this.saveWorkbook(workbook, fileName);
  }

  private prepareDataForExport(data: any[], columns: ColDef[]): any[] {
    const exportableColumns = columns.filter(col => col.field && col.headerName && !col.hide);
    const isAssetManagement = this.isAssetManagementExport(columns, data);

    return data.map((row, idx) => {
      const exportRow: Record<string, any> = { 'S.No.': String(idx + 1) };
      let employeeColumnsAdded = false;

      for (const col of exportableColumns) {
        const headerName = col.headerName!;
        let fieldValue: any;

        // Special handling for 'resource' field which might be stored as 'name'
        if (col.field === 'resource' && !row.hasOwnProperty('resource') && row.hasOwnProperty('name')) {
          fieldValue = this.formatValue(row['name']);
        } else {
          fieldValue = this.formatValue(row[col.field!]);
        }

        // Add the main column
        exportRow[headerName] = fieldValue;

        // For asset-management exports, add Employee ID and Designation after Resource column
        if (isAssetManagement && (col.field === 'firstName' || col.field === 'resource') && col.headerName === 'Resource' && !employeeColumnsAdded) {
          exportRow['Employee ID'] = row.employeeId || row.empId || row.id || '';
          exportRow['Designation'] = row.designation || row.jobTitle || row.position || '';
          employeeColumnsAdded = true;
        }
      }

      // If it's asset-management but no Resource column found, add employee columns at the end
      if (isAssetManagement && !employeeColumnsAdded) {
        exportRow['Employee ID'] = row.employeeId || row.empId || row.id || '';
        exportRow['Designation'] = row.designation || row.jobTitle || row.position || '';
      }

      return exportRow;
    });
  }

  private isAssetManagementExport(columns: ColDef[], data: any[]): boolean {
    // Check if this is an asset-management related export by looking for common asset-management fields
    const assetManagementFields = [
      'employeeId', 'empId', 'laptopSerialNumber', 'chargerSerialNumber',
      'assetId', 'assetType', 'assetStatus', 'allocation', 'lastHireDate',
      'dateOfJoining', 'workLocation', 'department', 'managerId'
    ];

    // Check columns for asset-management specific fields
    const hasAssetColumns = columns.some(col =>
      col.field && assetManagementFields.includes(col.field)
    );

    // Check data for asset-management specific properties
    const hasAssetData = data.length > 0 && data.some(row =>
      assetManagementFields.some(field => row.hasOwnProperty(field))
    );

    return hasAssetColumns || hasAssetData;
  }

  private formatValue(value: any): any {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toLocaleDateString();
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  }

  private createWorkbook(data: any[], sheetName: string): XLSX.WorkBook {
    const wb = XLSX.utils.book_new();

    if (!data.length) {
      const ws = XLSX.utils.json_to_sheet([]);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      return wb;
    }
    const headers = Object.keys(data[0]);

    const aoa: any[][] = [
      headers,
      ...data.map(row =>
        headers.map(h =>
          CENTER_HEADERS.has(h) ? padCenter(row[h]) : row[h]
        )
      )
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = headers.map(h => ({
      width: CENTER_HEADERS.has(h)
        ? CENTER_COL_WIDTH
        : Math.max(
            h.length,
            ...data.map(row => (row[h] != null ? String(row[h]).length : 0)),
            14
          )
    }));

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    return wb;
  }

  private saveWorkbook(wb: XLSX.WorkBook, fileName: string): void {
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`
    );
  }

  handleExport(selectedRows: any[], columns: any[], componentType: string): void {
    if (!selectedRows || selectedRows.length === 0) {
      this.alertService.showWarningAlert(
        'You haven\'t selected any profiles.',
        'Please select profiles to proceed with the download.'
      );
      return;
    }
    this.openDownloadConfirmDialog(selectedRows, columns, componentType);
  }

  private openDownloadConfirmDialog(selectedRows: any[], columns: any[], componentType: string): void {
    this.dialog.open(AddCommentsComponent, {
      data: {
        count: selectedRows.length,
        option: 'download',
        selectedCard: componentType,
        selectedRows: selectedRows,
        columns: columns
      },
    });
  }


}
